/**
 * Getting a message to Beacon over a connection that drops: a phone on mobile
 * data loses the stream mid-answer, or the request never leaves.
 *
 * A dropped stream is not a lost message. The server keeps the message as
 * soon as it arrives and finishes the answer whether or not anyone is still
 * reading, so the conversation is read again until the answer is there.
 * Only a message the server never received is sent again, and only twice:
 * sending one it already has would ask Beacon the same thing twice.
 *
 * Kept free of React and of the app's path aliases so it can be tested on
 * its own.
 */

/** What the person reads when the connection, not Beacon, failed. */
export const CONNECTION_LOST = "Lost connection to Beacon. Your message wasn't lost."

/**
 * A failure of the connection rather than an answer from the server: fetch
 * rejecting ("Failed to fetch", Safari's "Load failed", a stream ending in
 * "network error"), or a gateway giving up (502, 503, 504).
 */
export function isConnectionError(error: unknown): boolean {
  if (!(error instanceof Error)) return false
  if (error.name === 'AbortError') return false
  if (error.name === 'TypeError') return true
  return /network|failed to fetch|load failed|connection (was )?(lost|reset|closed)|timed? ?out|\b50[234]\b/i.test(
    error.message,
  )
}

/** A failure as a sentence for the screen: never "TypeError: …", never a stack. */
export function friendlyError(error: unknown, fallback = 'Something went wrong. Try again.') {
  if (isConnectionError(error)) return CONNECTION_LOST
  const message = error instanceof Error ? error.message.trim() : ''
  return message || fallback
}

export const sleep = (ms: number) => new Promise<void>((resolve) => setTimeout(resolve, ms))

/**
 * Runs something safe to repeat (a read), again after a connection failure:
 * twice more, a little later each time. Anything else is thrown at once.
 */
export async function withRetry<T>(
  run: () => Promise<T>,
  {
    delays = [600, 1_800],
    wait = sleep,
  }: { delays?: number[]; wait?: (ms: number) => Promise<void> } = {},
): Promise<T> {
  for (let attempt = 0; ; attempt += 1) {
    try {
      return await run()
    } catch (error) {
      if (!isConnectionError(error) || attempt >= delays.length) throw error
      await wait(delays[attempt]!)
    }
  }
}

interface MessageLike {
  role: string
  content: string
  created_at: string
}

/**
 * Where a message got to, from the conversation as the server has it: not
 * there, there and waiting for its answer, or answered.
 */
export function deliveryOf(
  messages: MessageLike[],
  content: string,
  sentAt: number,
): 'missing' | 'arrived' | 'answered' {
  const sent = content.trim()
  // Clocks differ: a message stamped a little before it was sent is still this one.
  const since = sentAt - 2 * 60_000
  let index = -1
  messages.forEach((message, position) => {
    if (
      message.role === 'USER' &&
      message.content.trim() === sent &&
      new Date(message.created_at).getTime() >= since
    ) {
      index = position
    }
  })
  if (index === -1) return 'missing'
  return messages.slice(index + 1).some((message) => message.role === 'ASSISTANT')
    ? 'answered'
    : 'arrived'
}

export interface DeliveryDeps {
  /** Sends the message and reads the answer as it streams. */
  stream: () => Promise<void>
  /** The conversation as the server has it now. */
  read: () => Promise<{ messages: MessageLike[] }>
  wait?: (ms: number) => Promise<void>
  /** The connection dropped and Helm is finding out what reached Beacon. */
  onReconnecting?: () => void
}

/** Sending again a message that never arrived: after these pauses, then no more. */
const RESEND_DELAYS = [1_000, 3_000]
/** Reading again for an answer that is still being written: about a minute in all. */
const ANSWER_POLLS = [
  1_000, 2_000, 3_000, 4_000, 5_000, 5_000, 5_000, 5_000, 5_000, 5_000, 5_000, 5_000, 5_000,
]

/**
 * Sends a message, and when the connection fails finds out what reached
 * Beacon: answered (done), arrived (wait for the answer), or missing (send
 * again). Resolves once the answer is on the server; throws the connection
 * error when it never got there or no answer came, and any other error as it
 * is.
 */
export async function deliverMessage(
  content: string,
  sentAt: number,
  { stream, read, wait = sleep, onReconnecting }: DeliveryDeps,
  /** Sent before, and the connection lost: find out where it got to before sending again. */
  { resume = false }: { resume?: boolean } = {},
): Promise<'streamed' | 'recovered'> {
  for (let resend = 0; ; resend += 1) {
    let dropped: unknown = new TypeError('network error')
    if (!resume || resend > 0) {
      try {
        await stream()
        return 'streamed'
      } catch (error) {
        if (!isConnectionError(error)) throw error
        dropped = error
      }
    }
    onReconnecting?.()
    const state = await awaitAnswer(content, sentAt, read, wait)
    if (state === 'answered') return 'recovered'
    if (state === 'missing' && resend < RESEND_DELAYS.length) {
      await wait(RESEND_DELAYS[resend]!)
      continue
    }
    throw dropped
  }
}

/** Reads the conversation until the message is answered, or is plainly not there. */
async function awaitAnswer(
  content: string,
  sentAt: number,
  read: DeliveryDeps['read'],
  wait: (ms: number) => Promise<void>,
): Promise<'missing' | 'arrived' | 'answered' | 'unknown'> {
  let state: 'missing' | 'arrived' | 'answered' | 'unknown' = 'unknown'
  for (const pause of ANSWER_POLLS) {
    await wait(pause)
    try {
      state = deliveryOf((await read()).messages, content, sentAt)
    } catch (error) {
      if (!isConnectionError(error)) throw error
      continue
    }
    if (state !== 'arrived') return state
  }
  return state
}
